/**
 * KB-META
 * Purpose: App footer
 * Role: ui.component
 * EntryPoints: 
 * Inputs: 
 * Outputs: 
 * Validation: 
 * Dependencies: 
 * Contracts: 
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: 
 * Router: shard=partner-portal-cura; H2=component/app.footer; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { SafeHtmlPipe } from '../../shared/security/safe-html.pipe';

interface VersionHistoryEntry {
    version: string;
    date: string;
    title: string;
    summary: string;
    details?: string;
    changes: string[];
    type: 'feature' | 'fix' | 'improvement' | 'breaking' | 'refactor';
}

interface VersionHistoryData {
    entries: VersionHistoryEntry[];
}

interface RoadmapSection {
    title: string;
    items: string[];
    icon?: string;
}

interface RoadmapData {
    sections: RoadmapSection[];
}

interface DatePreset {
    label: string;
    value: string;
}

type TagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined;

interface TypeOption {
    label: string;
    value: string;
    icon: string;
    severity: TagSeverity;
}

@Component({
    standalone: true,
    selector: 'app-footer',
    imports: [
        CommonModule,
        FormsModule,
        DialogModule,
        ButtonModule,
        TagModule,
        TooltipModule,
        InputTextModule,
        CalendarModule,
        DropdownModule,
        MultiSelectModule,
        SafeHtmlPipe
    ],
    template: `
    <div class="layout-footer flex items-center justify-between px-3 py-2 text-sm">
        <div class="flex items-center gap-3">
            <span class="version-display cursor-pointer hover:underline" (click)="historyVisible = true" pTooltip="Click to view version history">
                Version {{ versionLabel }} © 2026 genusConnect® All rights reserved.
            </span>
            <button pButton type="button" icon="pi pi-sparkles" class="p-button-text p-button-sm p-button-rounded" pTooltip="Forward Looking Roadmap" (click)="roadmapVisible = true"></button>
        </div>
        <button pButton type="button" label="About" class="p-button-text p-button-sm" (click)="aboutVisible = true"></button>
    </div>

    <!-- Roadmap Dialog -->
    <p-dialog [(visible)]="roadmapVisible" [modal]="true" [style]="{width:'640px'}" [closable]="true" header="Future Roadmap" styleClass="roadmap-dialog">
        <div class="roadmap-content p-2">
            <div class="text-center mb-4">
                <i class="pi pi-sparkles text-4xl text-blue-500 mb-2"></i>
                <h3 class="text-xl font-bold m-0">Advancing the Platform</h3>
                <p class="text-sm text-gray-500 mt-1">Our vision for the future of engagement and AI</p>
            </div>

            <div class="space-y-4">
                <div *ngFor="let section of roadmapData?.sections; let i = index" class="roadmap-section border-round overflow-hidden border-1 border-gray-200 dark:border-gray-700">
                    <div 
                        class="section-header p-3 cursor-pointer flex items-center justify-between bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        (click)="toggleRoadmapSection(i)">
                        <h4 class="text-base font-bold m-0 flex items-center gap-2 text-blue-700 dark:text-blue-400">
                            <i class="pi" [ngClass]="section.icon || 'pi-chevron-circle-right'"></i>
                            {{ section.title }}
                        </h4>
                        <i class="pi transition-transform duration-200" [ngClass]="isSectionExpanded(i) ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
                    </div>
                    
                    <div class="section-body p-3 bg-gray-50 dark:bg-gray-900/30" *ngIf="isSectionExpanded(i)">
                        <ul class="list-none p-0 m-0 space-y-3">
                            <li *ngFor="let item of section.items" class="flex items-start gap-3 bg-white dark:bg-gray-800 p-3 rounded-lg border-left-3 border-blue-500 shadow-sm">
                                <i class="pi pi-check-circle text-blue-500 mt-1 flex-shrink-0"></i>
                                <span class="text-sm leading-relaxed text-gray-700 dark:text-gray-200">{{ item }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        <ng-template pTemplate="footer">
            <button pButton type="button" label="Close" class="p-button-text" (click)="roadmapVisible = false"></button>
        </ng-template>
    </p-dialog>

    <!-- About Dialog -->
    <p-dialog [(visible)]="aboutVisible" [modal]="true" [style]="{width:'520px'}" [closable]="true" header="About">
        <div class="leading-relaxed text-gray-700 dark:text-gray-200">
            <div class="text-center mb-4">
                <div class="text-lg font-semibold text-gray-900 dark:text-gray-50">Centered on purpose</div>
                <div class="text-sm text-gray-600 dark:text-gray-300">
                    Connecting health systems, communities, families, and payers
                </div>
            </div>

            <div class="space-y-3">
                <p class="text-sm text-gray-700 dark:text-gray-200">
                    Powered by AI and an evidence‑based approach that harnesses:
                </p>
                <ul class="list-disc pl-5 space-y-1 text-sm text-gray-700 dark:text-gray-200">
                    <li><span class="font-semibold text-gray-900 dark:text-gray-50">Machine learning</span></li>
                    <li><span class="font-semibold text-gray-900 dark:text-gray-50">Custom language models</span></li>
                    <li><span class="font-semibold text-gray-900 dark:text-gray-50">Reinforcement learning</span></li>
                </ul>
                <p class="text-sm text-gray-700 dark:text-gray-200">
                    We capture and reinforce the wisdom of teams on the frontline—paired to the mission and needs of organizations—while advancing the evolving demands of healthcare.
                </p>
            </div>
        </div>
        <ng-template pTemplate="footer">
            <button pButton type="button" label="Close" class="p-button-text" (click)="aboutVisible = false"></button>
        </ng-template>
    </p-dialog>

    <!-- Version History Dialog -->
    <p-dialog 
        [(visible)]="historyVisible" 
        [modal]="true" 
        [style]="{width:'880px', maxHeight:'90vh'}" 
        [closable]="true" 
        header="Version History"
        styleClass="version-history-dialog">
        <div class="version-history-content">
            <!-- Current Version Hero -->
            <div class="current-version-hero">
                <div class="version-badge">
                    <i class="pi pi-tag"></i>
                    <span>Current Version</span>
                </div>
                <h2 class="version-number">{{ versionLabel }}</h2>
            </div>

            <!-- Search & Filter Bar -->
            <div class="filter-bar p-3 border-bottom-1 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sticky top-0 z-5">
                <div class="flex flex-wrap gap-2 align-items-center mb-2">
                    <div class="p-input-icon-left flex-1 min-w-200">
                        <i class="pi pi-search"></i>
                        <input 
                            pInputText 
                            type="text" 
                            class="w-full p-inputtext-sm" 
                            [(ngModel)]="searchQuery" 
                            placeholder="Search version details..." />
                    </div>
                    
                    <p-dropdown 
                        [options]="datePresets" 
                        [(ngModel)]="selectedPreset" 
                        optionLabel="label" 
                        optionValue="value"
                        placeholder="Time range"
                        styleClass="p-inputtext-sm min-w-150"
                        (onChange)="applyPreset($event.value)"
                        [showClear]="true"
                        appendTo="body">
                    </p-dropdown>

                    <p-multiSelect 
                        [options]="typeOptions" 
                        [(ngModel)]="selectedTypes" 
                        optionLabel="label" 
                        optionValue="value"
                        placeholder="Filter by type"
                        display="chip"
                        [maxSelectedLabels]="2"
                        styleClass="p-inputtext-sm min-w-180"
                        appendTo="body">
                        <ng-template let-option pTemplate="item">
                            <div class="flex align-items-center gap-2">
                                <i class="pi" [ngClass]="option.icon"></i>
                                <span>{{option.label}}</span>
                            </div>
                        </ng-template>
                    </p-multiSelect>

                    <div class="date-filter min-w-200">
                        <p-calendar 
                            [(ngModel)]="dateRange" 
                            selectionMode="range" 
                            [readonlyInput]="true" 
                            [showIcon]="true"
                            [showClear]="true"
                            placeholder="Custom date range"
                            styleClass="w-full p-inputtext-sm range-calendar"
                            (onSelect)="selectedPreset = null"
                            (onClear)="selectedPreset = null"
                            appendTo="body">
                        </p-calendar>
                    </div>

                    <button 
                        pButton 
                        type="button" 
                        icon="pi pi-filter-slash" 
                        label="Reset All" 
                        class="p-button-outlined p-button-sm p-button-secondary"
                        (click)="clearFilters()"
                        [disabled]="!searchQuery && !dateRange && !selectedPreset && selectedTypes.length === 0">
                    </button>
                </div>

                <!-- Impact Summary / Stats Strip -->
                <div class="filter-status mt-2 flex flex-wrap items-center justify-between gap-3" *ngIf="searchQuery || dateRange || selectedPreset || selectedTypes.length > 0">
                    <div class="flex flex-wrap items-center gap-2">
                        <p-tag [value]="filteredVersionHistory.length + ' Results Found'" severity="info" [rounded]="true"></p-tag>
                        <div class="stats-pills flex gap-1">
                            <p-tag *ngFor="let stat of filterStats" 
                                   [value]="stat.count + ' ' + (stat.label | titlecase)" 
                                   [severity]="stat.severity" 
                                   styleClass="text-xs">
                            </p-tag>
                        </div>
                    </div>
                    <div class="text-xs text-gray-500 font-medium" *ngIf="dateRange && dateRange[0]">
                        <i class="pi pi-calendar mr-1"></i>
                        {{ dateRange[0] | date:'MMM d, y' }} 
                        <span *ngIf="dateRange[1]"> - {{ dateRange[1] | date:'MMM d, y' }}</span>
                    </div>
                </div>
            </div>

            <!-- History Timeline -->
            <div class="history-timeline">
                @for (entry of filteredVersionHistory; track entry.version) {
                    <div class="history-entry" [class.latest]="$first && !searchQuery && !dateRange && !selectedPreset && selectedTypes.length === 0">
                        <div class="entry-header">
                            <div class="entry-version-info">
                                <span class="entry-version">v{{ entry.version }}</span>
                                <p-tag 
                                    [value]="entry.type | titlecase" 
                                    [severity]="getTypeSeverity(entry.type)"
                                    [rounded]="true">
                                </p-tag>
                            </div>
                            <span class="entry-date">{{ entry.date }}</span>
                        </div>
                        <h3 class="entry-title" [innerHTML]="highlightText(entry.title) | safeHtml"></h3>
                        <p class="entry-summary" [innerHTML]="highlightText(entry.summary) | safeHtml"></p>
                        
                        <div *ngIf="entry.details" class="entry-details-container mb-3">
                            <button 
                                pButton 
                                type="button" 
                                [label]="isDetailsExpanded(entry.version) ? 'Show less' : 'Read more details...'" 
                                [icon]="isDetailsExpanded(entry.version) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                                class="p-button-text p-button-sm p-0 mb-2 h-auto text-blue-600 font-semibold"
                                (click)="toggleDetails(entry.version)">
                            </button>
                            
                            <div *ngIf="isDetailsExpanded(entry.version)" 
                                 class="entry-details-content p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border-1 border-gray-200 dark:border-gray-700 text-sm animate-fade-in"
                                 [innerHTML]="formatDetails(entry.details) | safeHtml">
                            </div>
                        </div>

                        <ul class="entry-changes">
                            @for (change of entry.changes; track $index) {
                                <li [innerHTML]="highlightText(change) | safeHtml"></li>
                            }
                        </ul>
                    </div>
                }

                @if (filteredVersionHistory.length === 0) {
                    <div class="no-history">
                        <i class="pi pi-search-minus"></i>
                        <p>No matches found for your search criteria</p>
                        <button pButton type="button" label="Clear all filters" class="p-button-text p-button-sm mt-2" (click)="clearFilters()"></button>
                    </div>
                }
            </div>
        </div>
        <ng-template pTemplate="footer">
            <button pButton type="button" label="Close" icon="pi pi-times" class="p-button-text" (click)="historyVisible = false"></button>
        </ng-template>
    </p-dialog>
    `,
    styles: [`
        .version-display {
            transition: opacity 0.2s ease;
        }
        .version-display:hover {
            opacity: 0.8;
        }

        :host ::ng-deep .version-history-dialog {
            .p-dialog-content {
                padding: 0;
                overflow-y: auto;
                max-height: calc(90vh - 120px);
            }
            .p-dialog-header {
                background: linear-gradient(135deg, #1e40af 0%, #7c3aed 100%);
                color: white;
                border-bottom: none;
                .p-dialog-header-icons button {
                    color: white;
                    &:hover {
                        background: rgba(255,255,255,0.1);
                    }
                }
            }
        }

        .filter-bar {
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .version-history-content {
            background: var(--surface-ground);
        }

        .current-version-hero {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 1.5rem 2rem;
            background: linear-gradient(135deg, rgba(30, 64, 175, 0.08), rgba(124, 58, 237, 0.05));
            border-bottom: 1px solid var(--surface-border);
            text-align: center;
        }

        .version-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.35rem 0.75rem;
            background: rgba(30, 64, 175, 0.12);
            border: 1px solid rgba(30, 64, 175, 0.25);
            border-radius: 999px;
            font-size: 0.7rem;
            font-weight: 600;
            color: #1e40af;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            margin-bottom: 0.5rem;
            i { font-size: 0.75rem; }
        }

        .version-number {
            margin: 0;
            font-size: 1.75rem;
            font-weight: 700;
            background: linear-gradient(135deg, #1e40af, #7c3aed);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }

        .history-timeline {
            padding: 1rem 1.5rem 1.5rem;
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        .history-entry {
            background: var(--surface-card);
            border: 1px solid var(--surface-border);
            border-radius: 12px;
            padding: 1.25rem;
            transition: all 0.2s ease;

            &:hover {
                border-color: rgba(30, 64, 175, 0.3);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
            }

            &.latest {
                border-left: 4px solid #1e40af;
                background: linear-gradient(135deg, rgba(30, 64, 175, 0.03), transparent);
            }
        }

        .entry-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 0.75rem;
        }

        .entry-version-info {
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }

        .entry-version {
            font-size: 0.95rem;
            font-weight: 700;
            color: var(--text-color);
            font-family: 'JetBrains Mono', 'Fira Code', monospace;
        }

        .entry-date {
            font-size: 0.8rem;
            color: var(--text-color-secondary);
        }

        .entry-title {
            margin: 0 0 0.5rem;
            font-size: 1rem;
            font-weight: 600;
            color: var(--text-color);
        }

        .entry-summary {
            margin: 0 0 0.75rem;
            font-size: 0.875rem;
            color: var(--text-color-secondary);
            line-height: 1.5;
        }

        .entry-changes {
            margin: 0;
            padding-left: 1.25rem;
            li {
                font-size: 0.8rem;
                color: var(--text-color-secondary);
                line-height: 1.6;
                margin-bottom: 0.25rem;
                &:last-child { margin-bottom: 0; }
            }
        }

        .no-history {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 3rem;
            text-align: center;
            i {
                font-size: 2.5rem;
                color: var(--text-color-secondary);
                opacity: 0.4;
                margin-bottom: 1rem;
            }
            p {
                margin: 0;
                color: var(--text-color-secondary);
            }
        }

        ::ng-deep .search-highlight {
            background-color: rgba(255, 255, 0, 0.3);
            border-bottom: 1px solid orange;
            padding: 0 2px;
            border-radius: 2px;
        }

        /* Highlighting for range in calendar */
        :host ::ng-deep .range-calendar .p-datepicker-group {
            .p-highlight {
                background: #1e40af !important;
                color: #ffffff !important;
            }
            .p-datepicker-calendar td > span.p-highlight {
                border-radius: 50%;
            }
        }

        /* Dark mode support */
        :host-context(.dark) {
            .version-badge {
                background: rgba(96, 165, 250, 0.15);
                border-color: rgba(96, 165, 250, 0.3);
                color: #60a5fa;
            }
            .version-number {
                background: linear-gradient(135deg, #60a5fa, #a78bfa);
                -webkit-background-clip: text;
                background-clip: text;
            }
            .history-entry.latest {
                border-left-color: #60a5fa;
            }
            .filter-bar {
                background: #1e293b;
            }
            ::ng-deep .search-highlight {
                background-color: rgba(255, 255, 0, 0.2);
                color: #fff;
            }
        }

        :host ::ng-deep .roadmap-dialog {
            .p-dialog-header {
                background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
                color: white;
                .p-dialog-header-icons button {
                    color: white;
                    &:hover { background: rgba(255,255,255,0.1); }
                }
            }
            .p-dialog-content {
                background: #fafafa;
            }
        }

        :host-context(.dark) ::ng-deep .roadmap-dialog {
            .p-dialog-content {
                background: #0f172a;
            }
        }

        .section-header:hover {
            background-color: var(--surface-50);
        }

        .roadmap-section {
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            transition: all 0.2s ease;
        }

        .roadmap-section:hover {
            border-color: #3b82f6 !important;
        }

        .animate-fade-in {
            animation: fadeIn 0.3s ease-in-out;
        }

        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-5px); }
            to { opacity: 1; transform: translateY(0); }
        }

        .entry-details-content {
            line-height: 1.6;
            box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
        }
    `]
})
export class AppFooter implements OnInit {
    private readonly http = inject(HttpClient);

    aboutVisible = false;
    historyVisible = false;
    roadmapVisible = false;
    versionLabel = '--';
    versionHistory: VersionHistoryEntry[] = [];
    roadmapData: RoadmapData | null = null;
    expandedRoadmapSections: Set<number> = new Set([0]); // Expand first section by default
    expandedDetails: Set<string> = new Set(); // Track expanded version details by version number

    searchQuery: string = '';
    dateRange: Date[] | null = null;
    selectedPreset: string | null = null;
    selectedTypes: string[] = [];

    datePresets: DatePreset[] = [
        { label: 'Last 48 hours', value: '48h' },
        { label: 'Last 7 days', value: '7d' },
        { label: 'Last 30 days', value: '30d' },
        { label: 'Last 90 days', value: '90d' },
        { label: 'Year to date', value: 'ytd' }
    ];

    typeOptions: TypeOption[] = [
        { label: 'Features', value: 'feature', icon: 'pi pi-star-fill', severity: 'success' },
        { label: 'Fixes', value: 'fix', icon: 'pi pi-wrench', severity: 'info' },
        { label: 'Improvements', value: 'improvement', icon: 'pi pi-arrow-up-right', severity: 'warn' },
        { label: 'Refactors', value: 'refactor', icon: 'pi pi-sync', severity: 'contrast' },
        { label: 'Breaking Changes', value: 'breaking', icon: 'pi pi-exclamation-triangle', severity: 'danger' }
    ];

    ngOnInit(): void {
        const timestamp = Date.now();
        
        // Load current version
        this.http
            .get<{ version?: string }>(`/assets/json/version.json?ts=${timestamp}`)
            .subscribe({
                next: (data) => {
                    this.versionLabel = data?.version?.trim() || this.versionLabel;
                },
            });

        // Load version history
        this.http
            .get<VersionHistoryData>(`/assets/json/version-history.json?ts=${timestamp}`)
            .subscribe({
                next: (data) => {
                    this.versionHistory = data?.entries || [];
                },
            });

        // Load roadmap data
        this.http
            .get<RoadmapData>(`/assets/json/future-roadmap.json?ts=${timestamp}`)
            .subscribe({
                next: (data) => {
                    this.roadmapData = data;
                },
            });
    }

    applyPreset(value: string): void {
        if (!value) {
            this.dateRange = null;
            return;
        }

        const now = new Date();
        let start = new Date();
        let end = new Date();

        switch (value) {
            case '48h':
                start.setHours(now.getHours() - 48);
                break;
            case '7d':
                start.setDate(now.getDate() - 7);
                break;
            case '30d':
                start.setDate(now.getDate() - 30);
                break;
            case '90d':
                start.setDate(now.getDate() - 90);
                break;
            case 'ytd':
                start = new Date(now.getFullYear(), 0, 1);
                break;
        }

        this.dateRange = [start, end];
    }

    get filteredVersionHistory(): VersionHistoryEntry[] {
        let filtered = this.versionHistory;

        // Date range filtering
        if (this.dateRange && this.dateRange[0]) {
            const start = this.dateRange[0];
            const end = this.dateRange[1] || this.dateRange[0];
            // Create a copy to avoid mutating the selected date range
            const compareStart = new Date(start);
            const compareEnd = new Date(end);
            compareStart.setHours(0, 0, 0, 0);
            compareEnd.setHours(23, 59, 59, 999);

            filtered = filtered.filter(entry => {
                const entryDate = this.parseDate(entry.date);
                if (!entryDate) return true;
                return entryDate >= compareStart && entryDate <= compareEnd;
            });
        }

        // Type filtering
        if (this.selectedTypes && this.selectedTypes.length > 0) {
            filtered = filtered.filter(entry => this.selectedTypes.includes(entry.type));
        }

        // Search query filtering
        if (this.searchQuery && this.searchQuery.trim()) {
            const query = this.searchQuery.toLowerCase().trim();
            filtered = filtered.filter(entry => {
                const inTitle = entry.title.toLowerCase().includes(query);
                const inSummary = entry.summary.toLowerCase().includes(query);
                const inChanges = entry.changes.some(c => c.toLowerCase().includes(query));
                const inVersion = entry.version.toLowerCase().includes(query);
                return inTitle || inSummary || inChanges || inVersion;
            });
        }

        return filtered;
    }

    get filterStats(): Array<{ label: string, count: number, severity: TagSeverity }> {
        const results = this.filteredVersionHistory;
        const statsMap = new Map<string, number>();
        
        results.forEach(entry => {
            statsMap.set(entry.type, (statsMap.get(entry.type) || 0) + 1);
        });

        return this.typeOptions
            .filter(opt => statsMap.has(opt.value))
            .map(opt => ({
                label: opt.label,
                count: statsMap.get(opt.value) || 0,
                severity: opt.severity
            }));
    }

    private parseDate(dateStr: string): Date | null {
        // Formats: "Dec 23", "Jan 6", "Dec 19 2025", "Nov 30 2025", "Dec 4 2025"
        try {
            const parts = dateStr.split(' ').filter(Boolean);
            const month = parts[0];
            const day = Number.parseInt(parts[1], 10);
            if (!Number.isFinite(day)) return null;

            const now = new Date();
            let year = parts[2] ? Number.parseInt(parts[2], 10) : now.getFullYear();

            const months: { [key: string]: number } = {
                'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
                'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
            };

            const monthIndex = months[month];
            if (monthIndex === undefined) return null;

            // If year isn't provided, infer it. Version history entries should not be future-dated;
            // when crossing year boundaries (e.g., Jan current month), "Dec 23" should resolve to last year.
            if (!parts[2]) {
                const currentMonth = now.getMonth();
                if (monthIndex > currentMonth) {
                    year = year - 1;
                }
            }

            return new Date(year, monthIndex, day);
        } catch {
            return null;
        }
    }

    highlightText(text: string): string {
        if (!this.searchQuery || !this.searchQuery.trim()) return text;
        const query = this.searchQuery.trim();
        const regex = new RegExp(`(${query})`, 'gi');
        return text.replace(regex, '<span class="search-highlight">$1</span>');
    }

    clearFilters(): void {
        this.searchQuery = '';
        this.dateRange = null;
        this.selectedPreset = null;
        this.selectedTypes = [];
    }

    toggleRoadmapSection(index: number): void {
        if (this.expandedRoadmapSections.has(index)) {
            this.expandedRoadmapSections.delete(index);
        } else {
            this.expandedRoadmapSections.add(index);
        }
    }

    isSectionExpanded(index: number): boolean {
        return this.expandedRoadmapSections.has(index);
    }

    toggleDetails(version: string): void {
        if (this.expandedDetails.has(version)) {
            this.expandedDetails.delete(version);
        } else {
            this.expandedDetails.add(version);
        }
    }

    isDetailsExpanded(version: string): boolean {
        return this.expandedDetails.has(version);
    }

    formatDetails(text: string): string {
        if (!text) return '';
        
        let formatted = text
            // Headers
            .replace(/^### (.*$)/gim, '<h5 class="font-bold text-gray-900 dark:text-gray-100 mt-3 mb-2 border-bottom-1 border-gray-200 dark:border-gray-700 pb-1">$1</h5>')
            .replace(/^#### (.*$)/gim, '<h6 class="font-bold text-gray-800 dark:text-gray-200 mt-2 mb-1">$1</h6>')
            // Bold
            .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900 dark:text-gray-100">$1</strong>')
            // Bullets
            .replace(/^\* (.*$)/gim, '<div class="flex items-start gap-2 mb-1"><i class="pi pi-circle-fill mt-1 text-xs" style="font-size: 0.5rem; color: #3b82f6;"></i><span>$1</span></div>')
            // Newlines
            .replace(/\n/g, '<br/>');
            
        return formatted;
    }

    getTypeSeverity(type: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
        switch (type) {
            case 'feature': return 'success';
            case 'fix': return 'info';
            case 'improvement': return 'warn';
            case 'breaking': return 'danger';
            case 'refactor': return 'contrast';
            default: return 'secondary';
        }
    }
}
